import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Base
import LetterIcon from '../../v5/base/letter_icon';

export default class Avatar extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    imgClassName: PropTypes.string,
    onClick: PropTypes.func,
    src: PropTypes.string,
    alt: PropTypes.string,
    defaultAvatar: PropTypes.string,
    defaultOrganizationAvatar: PropTypes.string,
    defaultType: PropTypes.string,
    size: PropTypes.oneOf(['xxsm', 'xsm', 'sm', 'md', 'lg', 'xlg', '2xl']),
    square: PropTypes.bool,
    isManagedConsultant: PropTypes.bool,
    isOrganization: PropTypes.bool,
    bottomLeftAction: PropTypes.any,
    bottomRightAction: PropTypes.any,
    hoverable: PropTypes.bool,
    letterText: PropTypes.string,
    letterMaxChars: PropTypes.number,
  }

  static defaultProps = {
    onClick: () => { },
    alt: 'avatar',
    defaultAvatar: '/blankuseravatar.png',
    defaultOrganizationAvatar: '/blankorganizationavatar.png',
    defaultType: 'image/png',
    isOrganization: false,
    hoverable: false,
    letterMaxChars: 2,
  }

  constructor(props) {
    super(props);
    this.state = {
      errored: false,     // real image failed
      usedFallback: false // we already tried fallback image
    };
  }

  componentDidUpdate(prevProps) {
    // If the main src or managed flag changes, reset error/fallback state
    if (
      prevProps.src !== this.props.src ||
      prevProps.isManagedConsultant !== this.props.isManagedConsultant
    ) {
      this.setState({ errored: false, usedFallback: false });
    }
  }

  get classNames() {
    const { className, size, square, hoverable } = this.props;
    return classNames('avatar', {
      [className]: className,
      'is-2xl': size === '2xl',
      'is-xlarge': size === 'xlg',
      'is-large': size === 'lg',
      'is-medium': size === 'md',
      'is-small': size === 'sm',
      'is-xsmall': size === 'xsm',
      'is-xxsmall': size === 'xxsm',
      'is-square': square,
      'is-hoverable': hoverable,
    });
  }

  get imgClassNames() {
    const { imgClassName, size, square } = this.props;

    if (!imgClassName && !size) {
      return '';
    }

    return classNames('avatar', {
      [imgClassName]: imgClassName,
      'is-xlarge': size === 'xlg',
      'is-large': size === 'lg',
      'is-medium': size === 'md',
      'is-small': size === 'sm',
      'is-xsmall': size === 'xsm',
      'is-xxsmall': size === 'xxsm',
      'is-square': square,
    });
  }

  get baseSrc() {
    const { isManagedConsultant, src } = this.props;
    if (isManagedConsultant) return '/managedconsultantavatar.png';
    return src || null;
  }

  get fallbackSrc() {
    const { isOrganization, defaultAvatar, defaultOrganizationAvatar } = this.props;
    return isOrganization ? defaultOrganizationAvatar : defaultAvatar;
  }

  // Map Avatar size to LetterIcon size
  get letterIconSize() {
    const { size } = this.props;
    if (size === 'xxsm') return 'xsm';
    if (size === 'xsm') return 'xsm';
    if (size === 'sm') return 'sm';
    if (size === 'md') return 'md';
    if (size === 'lg') return 'lg';
    if (size === 'xlg' || size === '2xl') return 'xlg';
    return 'md';
  }

  handleError = () => {
    const { letterText } = this.props;

    if (letterText) {
      // We have a name, so prefer initials over any fallback image
      this.setState({ errored: true });
    } else {
      // No name; use fallback image once
      this.setState((prev) => {
        if (prev.usedFallback) return prev; // avoid loops
        return { ...prev, usedFallback: true };
      });
    }
  }

  render() {
    const {
      onClick,
      alt,
      defaultType,
      bottomRightAction,
      bottomLeftAction,
      letterText,
      letterMaxChars,
    } = this.props;
    const { errored, usedFallback } = this.state;

    const baseSrc = this.baseSrc;
    const fallbackSrc = this.fallbackSrc;

    // Show initials if:
    // - we have a name AND
    // - the real image has errored OR there's no real image at all
    const shouldShowInitials = !!letterText && (errored || !baseSrc);

    let imgSrc = null;
    if (!shouldShowInitials) {
      if (baseSrc && !usedFallback) {
        imgSrc = baseSrc;
      } else {
        imgSrc = fallbackSrc;
      }
    }

    return (
      <div className={this.classNames}>
        {shouldShowInitials ? (
          <LetterIcon
            text={letterText}
            size={this.letterIconSize}
            maxChars={letterMaxChars}
          />
        ) : (
          <img
            src={imgSrc}
            onClick={onClick}
            type={defaultType}
            alt={alt}
            className={this.imgClassNames}
            onError={this.handleError}
          />
        )}
        <div className="hover-overlay" />
        {bottomLeftAction && (
          <div className="bottom-left-action">
            {bottomLeftAction}
          </div>
        )}
        {bottomRightAction && (
          <div className="bottom-right-action">
            {bottomRightAction}
          </div>
        )}
      </div>
    );
  }
}